import React from 'react'
import { theme } from '../../styles/theme'

export type BadgeVariant = 'success' | 'warning' | 'info' | 'ai' | 'error' | 'neutral'
export type BadgeSize = 'sm' | 'md' | 'lg'

interface BadgeProps {
  children: React.ReactNode
  variant?: BadgeVariant
  size?: BadgeSize
  icon?: React.ReactNode
  className?: string
}

export const Badge: React.FC<BadgeProps> = ({
  children,
  variant = 'neutral',
  size = 'md',
  icon,
  className = '',
}) => {
  const variantStyles: Record<BadgeVariant, React.CSSProperties> = {
    success: {
      backgroundColor: '#ECFDF5',
      color: theme.colors.success,
      border: `1px solid ${theme.colors.successLight}`,
    },
    warning: {
      backgroundColor: '#FFF7ED',
      color: theme.colors.warning,
      border: `1px solid ${theme.colors.warningLight}`,
    },
    info: {
      backgroundColor: '#EFF6FF',
      color: theme.colors.info,
      border: `1px solid ${theme.colors.primaryLight}`,
    },
    ai: {
      background: theme.ai.gradient,
      color: theme.colors.white,
      border: 'none',
    },
    error: {
      backgroundColor: '#FEF2F2',
      color: theme.colors.error,
      border: '1px solid #FECACA',
    },
    neutral: {
      backgroundColor: theme.colors.gray100,
      color: theme.colors.gray700,
      border: `1px solid ${theme.colors.gray200}`,
    },
  }

  const sizeStyles: Record<BadgeSize, React.CSSProperties> = {
    sm: {
      fontSize: theme.typography.fontSize.xs,
      padding: '2px 8px',
      height: '20px',
    },
    md: {
      fontSize: theme.typography.fontSize.sm,
      padding: '4px 12px',
      height: '24px',
    },
    lg: {
      fontSize: theme.typography.fontSize.base,
      padding: '6px 16px',
      height: '32px',
    },
  }

  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing[1],
        borderRadius: theme.borderRadius.full,
        fontWeight: theme.typography.fontWeight.medium,
        whiteSpace: 'nowrap',
        transition: theme.transitions.fast,
        ...sizeStyles[size],
        ...variantStyles[variant],
      }}
    >
      {icon}
      {children}
    </span>
  )
}
